import * as React from 'react';
import { useDispatch, useIntl } from 'umi';
import { Button, Divider, Dropdown, Empty, Space, Tabs, Tag, Tooltip } from 'antd';
import { DownOutlined, PlusOutlined, SortDescendingOutlined } from '@ant-design/icons';
import VirtualizedList from 'rc-virtual-list';
import dayjs from 'dayjs';

import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { LeadDetailsModel, LeadModel } from '@/typings/models/Lead';
import { F } from '@/utils/intl';

import styles from './index.less';
import { CheckAccess } from '@/utils/access';
import PERMISSIONS from '@/constants/permissions';
import ServiceRequestItem from './ServiceRequestItem';

// Height estimates for the virtualized list — rc-virtual-list measures the real
// row heights as they render, these only seed the initial scroll math. The
// container itself uses maxHeight (fullHeight=false), so short lists shrink to
// their content instead of forcing an inner scrollbar.
const LIST_VIEW_ITEM_HEIGHT = 96;
const CARD_ITEM_HEIGHT = 200;
const MAX_LIST_HEIGHT = 600;

/**
 * Virtualized (rc-virtual-list) — a lead can carry hundreds of occurrences of a
 * recurring series, and scroll-based pagination will hook in here later. Only
 * the visible rows are mounted; the list shrinks to its content when short.
 */
export function ServiceRequestList({ serviceRequests, lead, request, ghostActions = false, showLead = false, onItemClick, listView = false }: {
  request: () => void,
  lead: LeadModel,
  ghostActions?: boolean,
  showLead?: boolean,
  listView?: boolean,
  serviceRequests: ServiceRequestModel[],
  onItemClick?: (serviceRequest: ServiceRequestModel) => void,
}) {
  const itemHeight = listView ? LIST_VIEW_ITEM_HEIGHT : CARD_ITEM_HEIGHT;
  return (
    <VirtualizedList
      data={serviceRequests}
      height={MAX_LIST_HEIGHT}
      fullHeight={false}
      itemHeight={itemHeight}
      itemKey="id"
    >
      {(serviceRequest) => (
        <div key={serviceRequest.id}>
          <ServiceRequestItem
            serviceRequest={serviceRequest as ServiceRequestModel}
            lead={lead || serviceRequest.lead}
            onRefresh={request}
            ghostActions={ghostActions}
            showLead={showLead}
            listView={listView}
            onItemClick={onItemClick ? () => onItemClick(serviceRequest) : undefined}
          />
        </div>
      )}
    </VirtualizedList>
  );
}


const APPOINTMENT_DATE_FILTER = {
  UPCOMING: 'upcoming',
  PAST: 'past',
  ALL: 'all',
};

const SORT_BY = {
  APPOINTMENT_DATE: 'appointment_date',
  CREATION_DATE: 'creation_date',
};

export function EntityViewServiceRequestList({ leadDetails, events, request, addDisabled = false, listView = false }){
  const dispatch = useDispatch();
  const intl = useIntl();
  const [appointmentDate, setAppointmentDate] = React.useState(APPOINTMENT_DATE_FILTER.UPCOMING);
  const [sortBy, setSortBy] = React.useState(SORT_BY.APPOINTMENT_DATE);

  const openAppointmentDetails = (serviceRequest: ServiceRequestModel) => {
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: { appointmentId: serviceRequest.id },
    });
  };

  // Sort key: appointment date (start time) or creation date, each falling
  // back to the other when missing (calendar events always have a start).
  const getSortTime = (serviceRequest: ServiceRequestModel) => {
    const date =
      sortBy === SORT_BY.CREATION_DATE
        ? serviceRequest.created || serviceRequest.utc_start_time
        : serviceRequest.utc_start_time || serviceRequest.created;
    return date ? new Date(date).getTime() : 0;
  };

  const allServiceRequests = [...(events || [])].sort((a, b) => getSortTime(b) - getSortTime(a));

  const sortOptions = [
    {
      key: SORT_BY.APPOINTMENT_DATE,
      label: intl.formatMessage({
        id: 'pages.leads.serviceRequests.sortByAppointmentDate',
        defaultMessage: 'Appointment date',
      }),
    },
    {
      key: SORT_BY.CREATION_DATE,
      label: intl.formatMessage({
        id: 'pages.leads.serviceRequests.sortByCreationDate',
        defaultMessage: 'Creation date',
      }),
    },
  ];
  const activeSortOption = sortOptions.find((option) => option.key === sortBy) || sortOptions[0];

  const onActionEnd = () => {
    request();
    dispatch({
      type: 'serviceRequest/updateFetchCalendarViewAt',
    });
  };
  const upcomingServiceRequests = allServiceRequests.filter(
    (serviceRequest) => {
      if (serviceRequest.utc_start_time)
        return dayjs(serviceRequest.utc_start_time).isAfter(dayjs());
      else if (serviceRequest.created)
        return dayjs(serviceRequest.created).isAfter(dayjs());
    },
  );
  const pastServiceRequests = allServiceRequests.filter((serviceRequest) => {
      if (serviceRequest.utc_start_time)
        return dayjs(serviceRequest.utc_start_time).isBefore(dayjs());
      else if (serviceRequest.created)
        return dayjs(serviceRequest.created).isBefore(dayjs());
    },
  );

  const openAddServiceRequestDrawer = () => {
    dispatch({
      type: 'serviceRequest/toggleCreateServiceRequestDrawer',
      payload: {
        openAppointmentDetails: true,
        lead: leadDetails,
        then: onActionEnd,
      },
    });
  };

  return (
    <div>
      <div className={styles.leadDetailsServiceRequestHeader}>
        <Tabs
          size={'small'}
          className={styles.serviceRequestTabs}
          activeKey={appointmentDate}
          onChange={setAppointmentDate}
          items={[
            {
              label: intl.formatMessage({
                id: 'pages.leads.serviceRequests.upcoming',
                defaultMessage: 'Upcoming',
              }),
              key: APPOINTMENT_DATE_FILTER.UPCOMING,
            },
            {
              label: intl.formatMessage({
                id: 'pages.leads.serviceRequests.past',
                defaultMessage: 'Past',
              }),
              key: APPOINTMENT_DATE_FILTER.PAST,
            },
            {
              label: intl.formatMessage({
                id: 'common.actions.all',
                defaultMessage: 'All',
              }),
              key: APPOINTMENT_DATE_FILTER.ALL,
            },
          ]}
          tabBarExtraContent={
            <Space size={4}>
              <Dropdown
                trigger={['click']}
                menu={{
                  selectable: true,
                  selectedKeys: [sortBy],
                  items: [
                    {
                      type: 'group',
                      label: intl.formatMessage({
                        id: 'pages.leads.serviceRequests.sortBy',
                        defaultMessage: 'Sort by',
                      }),
                      children: sortOptions.map((option) => ({ key: option.key, label: option.label })),
                    },
                  ],
                  onClick: ({ key }) => setSortBy(key),
                }}
              >
                <Button type="text" size="small" icon={<SortDescendingOutlined />}>
                  {activeSortOption.label}
                  <DownOutlined />
                </Button>
              </Dropdown>
              {!addDisabled && (
                <CheckAccess
                  fallback={<div />}
                  access={[
                    PERMISSIONS.ServiceRequests.CREATE,
                    PERMISSIONS.BUSINESS_PARTNER,
                  ]}
                >
                  <Tooltip title={<F id={'pages.lead.action.bookAppointment'} defaultMessage={'Book Appointment'} />}>
                    <Button
                      type={'primary'}
                      variant={'solid'}
                      size={'small'}
                      icon={<PlusOutlined />}
                      onClick={openAddServiceRequestDrawer}
                    />
                  </Tooltip>
                </CheckAccess>
              )}
            </Space>
          }
        />
      </div>
      {
        appointmentDate === APPOINTMENT_DATE_FILTER.UPCOMING ? (
          upcomingServiceRequests.length > 0 ? <ServiceRequestList
            request={request}
            lead={leadDetails}
            listView={listView}
            serviceRequests={upcomingServiceRequests}
            onItemClick={openAppointmentDetails}
          /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />) : appointmentDate === APPOINTMENT_DATE_FILTER.PAST ? (
            pastServiceRequests.length > 0 ? <ServiceRequestList
              request={request}
              lead={leadDetails}
              listView={listView}
              serviceRequests={pastServiceRequests}
              onItemClick={openAppointmentDetails}
            /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />)
          : (upcomingServiceRequests.length + pastServiceRequests.length === 0) ?
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> : <>
              <Space style={{ marginBottom: 16 }} />
              <ServiceRequestList
                request={request}
                lead={leadDetails}
                listView={listView}
                serviceRequests={upcomingServiceRequests}
                onItemClick={openAppointmentDetails}
              />
              <Divider orientation={'center'}>
                <Tag className={styles.activityDate}><F
                  id={'pages.leads.serviceRequests.pastServiceRequests'}
                  defaultMessage={'Past requests'}
                />
                </Tag>
              </Divider>
              <ServiceRequestList
                request={request}
                lead={leadDetails}
                listView={listView}
                serviceRequests={pastServiceRequests}
                onItemClick={openAppointmentDetails}
              />
            </>
      }

    </div>
  );
}

export default function LeadDetailsServiceRequests({ leadDetails, request, addDisabled = false }: {
  leadDetails: LeadDetailsModel,
  addDisabled?: boolean,
  request: () => void
}) {
  const events = (leadDetails?.service_requests || []).concat((leadDetails?.calendarEvents || [] as any).map(event => ({
    ...event,
    blocker: true
  })));
  return <EntityViewServiceRequestList leadDetails={leadDetails} events={events} request={request} addDisabled={addDisabled} />
}
